import type { Role } from '../proto';
import { contextHash, type PartialShare } from './poker-crypto';

export type MpsPlayerRole = Exclude<Role, 'spectator'>;
export interface MpsReveal {
  context: string;
  shares: string[];
  proofs: string[];
}

export function isMpsContext(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{64}$/.test(value);
}

export function mpsIdentity(room: string, board: number, hand: number, context: string, role: Role): string {
  return JSON.stringify([room, board, hand, context, role]);
}

export function mpsStorageKey(kind: 'seed' | 'shuffle', room: string, board: number, hand: number, context: string, role: MpsPlayerRole): string {
  if (!isMpsContext(context)) throw new Error('配牌contextが確定していません');
  return `mps-v2:${kind}:${mpsIdentity(room, board, hand, context, role)}`;
}

export function shuffleContext(context: string, role: MpsPlayerRole): string {
  if (!isMpsContext(context)) throw new Error('配牌contextが確定していません');
  return `${context}:shuffle:${role}`;
}

export function partialContext(context: string, role: MpsPlayerRole, index: number): string {
  if (!isMpsContext(context) || !Number.isInteger(index) || index < 0 || index >= 52) throw new Error('復号contextが不正です');
  return `${context}:partial:${role}:${index}`;
}

/** Only the currently requested street can be shared. Already public runout positions are safe retries. */
export function pokerBoardTargets(awaiting: string | null): number[] {
  switch (awaiting) {
    case 'flop': return [4, 5, 6];
    case 'turn': return [7];
    case 'river': return [8];
    case 'all_in_run_out': return [4, 5, 6, 7, 8];
    default: return [];
  }
}

export function pokerProvidePositions(awaiting: string | null, boardLength: number): number[] {
  if (!Number.isInteger(boardLength) || boardLength < 0 || boardLength > 5) return [];
  if (awaiting === 'all_in_run_out') return [4, 5, 6, 7, 8].slice(boardLength);
  const positions = pokerBoardTargets(awaiting);
  return positions[0] === 4 + boardLength ? positions : [];
}

export function revealForPositions(context: string, partials: ReadonlyMap<number, PartialShare>, positions: readonly number[]): MpsReveal | null {
  if (!isMpsContext(context) || positions.length === 0) return null;
  const selected = positions.map(position => partials.get(position));
  if (selected.some(partial => !partial)) return null;
  return { context, shares: selected.map(partial => partial!.share), proofs: selected.map(partial => partial!.proof) };
}

/** Round 2 proof is private to the host verifier; guest checks its own fixed output. */
export function canUseMpsFinalDeck(role: MpsPlayerRole, verifiedShuffle: boolean, finalDeck: readonly string[] | null, finalProof: string | null, ownGuestOutput: readonly string[] | null): boolean {
  if (!verifiedShuffle || !finalDeck || finalDeck.length !== 52) return false;
  if (role === 'host') return typeof finalProof === 'string' && finalProof.length > 0;
  return ownGuestOutput != null && ownGuestOutput.length === finalDeck.length && ownGuestOutput.every((card, index) => card === finalDeck[index]);
}

export async function localMpsContext(room: string, board: number, hand: number, game: string, hostSeat: string, firstSeat: string, hash: (bytes: Uint8Array) => Promise<string> = contextHash): Promise<string> {
  const tuple = JSON.stringify(['mixed-mps-context-v2', 'local', room, board, hand, game, 'local-you', 'local-cpu', hostSeat, firstSeat]);
  const digest = await hash(new TextEncoder().encode(tuple));
  if (!isMpsContext(digest)) throw new Error('配牌contextを生成できません');
  return digest;
}

/** Old in-progress decks cannot be changed to v2 without changing already dealt cards. */
export function localMpsNeedsRestart(snapshot: { finished: boolean; mpsVersion?: number; state: { boards?: { game_id: string; result?: unknown }[] } }): boolean {
  return !snapshot.finished && snapshot.mpsVersion !== 2 && (snapshot.state.boards ?? []).some(board =>
    (board.game_id === 'HeadsUpPoker' || board.game_id === 'BridgeNT') && board.result == null,
  );
}
